import { AlertTriangle, Calendar, FileText, KeyRound, MapPin, Phone, Users, XCircle } from 'lucide-react'
import type { OpenPermitListItem } from 'modules/permit/api/permitService'
import { Alert, AlertDescription } from 'shared/ui/alert'
import { Badge } from 'shared/ui/badge'
import { Button } from 'shared/ui/button'
import { Card, CardContent } from 'shared/ui/card'
import { Checkbox } from 'shared/ui/checkbox'
import { canOpenPermitToday, formatDateRange, formatSupervisorTime, resolvePermitTypeBadge } from './utils'

interface OpenPermitCardProps {
  permit: OpenPermitListItem
  selected: boolean
  onToggleSelected: (checked: boolean) => void
  onView: () => void
  onCancel: () => void
  onOpen: () => void
}

export function OpenPermitCard({ permit, selected, onToggleSelected, onView, onCancel, onOpen }: OpenPermitCardProps) {
  const canOpen = canOpenPermitToday(permit)
  const supervisorDetails = permit.supervisors ?? []
  const permitTypeBadge = resolvePermitTypeBadge(permit.permitTypeCode, permit.permitType)

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="space-y-4 p-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex gap-4">
            <Checkbox
              checked={selected}
              disabled={!canOpen}
              onCheckedChange={(checked) => onToggleSelected(checked === true)}
            />
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <Badge className={`border ${permitTypeBadge.className}`}>{permitTypeBadge.label}</Badge>
                <span className="font-medium text-slate-900">Permit No. : {permit.permitNumber || permit.permitId}</span>
                <span className="inline-flex items-center gap-2 text-slate-500"><Calendar className="h-4 w-4" />วันที่เข้าปฏิบัติงาน : {formatDateRange(permit.plannedStart, permit.plannedEnd)}</span>
              </div>

              <div>
                <p className="text-base font-medium text-slate-900">{permit.workDescription || permit.title || '-'}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {permit.priority ? <Badge variant="outline">{permit.priority}</Badge> : null}
                  {(permit.selectedSubActivities ?? []).map((subactivity) => (
                    <Badge key={`${permit.permitId}-${subactivity.subactivityId}`} variant="outline" className="border-slate-200 text-slate-700">
                      {subactivity.name || subactivity.code}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4" />{permit.location || '-'}</span>
                <span className="inline-flex items-center gap-2"><Users className="h-4 w-4" />จำนวนผู้เข้าปฏิบัติงาน : {permit.workersCount ?? 0} คน</span>
              </div>

              <div className="text-sm text-slate-600">
                <span className="font-medium text-slate-900">ผู้ขอ Permit :</span> {permit.requesterName || '-'} ({permit.requesterCompany || '-'})
                {permit.areaOwner ? <span> | ผู้ควบคุมงาน : {permit.areaOwner}</span> : null}
              </div>

              {permit.requesterPhone ? <div className="inline-flex items-center gap-2 text-xs text-slate-500"><Phone className="h-3.5 w-3.5" />{permit.requesterPhone}</div> : null}

              {!canOpen ? (
                <Alert className="border-amber-200 bg-amber-50 text-amber-800">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription>ไม่สามารถเปิด Permit ล่วงหน้าจากวันที่แจ้งขอมาได้</AlertDescription>
                </Alert>
              ) : null}

              {supervisorDetails.length > 0 ? (
                <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                  <p className="text-xs uppercase text-emerald-700">ผู้ปฏิบัติงาน / ผู้ควบคุมงานที่กำหนด</p>
                  <ul className="mt-2 space-y-1">
                    {supervisorDetails.map((sup) => {
                      const timeRange = formatSupervisorTime(sup.startTime, sup.endTime)
                      return (
                        <li key={`${permit.permitId}-${sup.employeeId}`} className="flex flex-wrap gap-2">
                          <span className="font-medium">{sup.employeeName || '-'}</span>
                          {timeRange ? <span className="text-xs text-emerald-800">({timeRange})</span> : null}
                        </li>
                      )
                    })}
                  </ul>
                </div>
              ) : null}
            </div>
          </div>

          <div className="flex min-w-[180px] flex-col gap-2">
            <Button variant="outline" onClick={onView}><FileText className="mr-2 h-4 w-4" />ดูรายละเอียด</Button>
            <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700" onClick={onCancel}><XCircle className="mr-2 h-4 w-4" />ยกเลิก</Button>
            <Button className="bg-emerald-600 text-white hover:bg-emerald-700" disabled={!canOpen} onClick={onOpen}><KeyRound className="mr-2 h-4 w-4" />เปิด Permit</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
